if (!customElements.get('tpt-tabs-component')) {
  customElements.define('tpt-tabs-component', class TptTabsComponent extends HTMLElement {
    constructor() {
      super();
      this.tabItems = this.querySelectorAll('tpt-tab-item');
      this.tabContents = this.querySelectorAll('tpt-tab-data');
      this.swipers = {};
      this.init();
    }

    init() {
      this.setupTabListeners();
      this.initializeSliders();
      this.showActiveTab();
    }

    setupTabListeners() {
      this.tabItems.forEach((tab) => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          this.switchTab(tab);
        });

        tab.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.switchTab(tab);
          }
        });
      });
    }

    switchTab(activeTab) {
      const targetId = activeTab.dataset.tabTarget;

      // Remove active class from all tabs and contents
      this.tabItems.forEach(tab => tab.classList.remove('active'));
      this.tabContents.forEach(content => content.classList.remove('active'));

      // Add active class to clicked tab
      activeTab.classList.add('active');

      // Show corresponding content
      const targetContent = this.querySelector(`[data-tab-container="${targetId}"]`);
      if (targetContent) {
        targetContent.classList.add('active');
        
        // Initialize slider for this tab if not already done
        const tabOrder = targetContent.querySelector('.tpt-tab-slider-container')?.dataset.tabOrder;
        if (tabOrder && !this.swipers[tabOrder]) {
          this.initializeSlider(targetContent);
        }
      }
    }

    showActiveTab() {
      const activeTab = this.querySelector('tpt-tab-item.active');
      if (activeTab) {
        this.switchTab(activeTab);
      }
    }

    initializeSliders() {
      // Initialize slider for the first (active) tab
      const firstActiveContent = this.querySelector('tpt-tab-data.active');
      if (firstActiveContent) {
        this.initializeSlider(firstActiveContent);
      }
    }

    initializeSlider(tabContent) {
      const sliderContainer = tabContent.querySelector('.tpt-tab-slider-container');
      if (!sliderContainer) return;

      const tabOrder = sliderContainer.dataset.tabOrder;
      const swiperElement = sliderContainer.querySelector('.swiper');
      const slides = swiperElement.querySelectorAll('.swiper-slide');
      const section = this.closest('.tpt-section');
      
      if (!swiperElement || this.swipers[tabOrder] || slides.length === 0) return;

      // Get section settings
      const showNavigation = section?.querySelector('[data-show-navigation]') ? 
        JSON.parse(section.querySelector('[data-show-navigation]').textContent) : true;
      const showPagination = section?.querySelector('[data-show-pagination]') ? 
        JSON.parse(section.querySelector('[data-show-pagination]').textContent) : true;
      const autoPlay = section?.querySelector('[data-auto-play]') ? 
        JSON.parse(section.querySelector('[data-auto-play]').textContent) : false;
      const autoPlayDelay = section?.querySelector('[data-auto-play-delay]') ? 
        parseInt(section.querySelector('[data-auto-play-delay]').textContent) * 1000 : 5000;
      const desktopColumns = section?.querySelector('[data-desktop-columns]') ?
        parseInt(section.querySelector('[data-desktop-columns]').textContent) : 3;
      const tabletColumns = section?.querySelector('[data-tablet-columns]') ?
        parseInt(section.querySelector('[data-tablet-columns]').textContent) : 2;
      const mobileColumns = section?.querySelector('[data-mobile-columns]') ?
        parseInt(section.querySelector('[data-mobile-columns]').textContent) : 1;
      const imageSize = section?.querySelector('[data-image-size]') ?
        parseInt(section.querySelector('[data-image-size]').textContent) : 50;

      // Swiper configuration
      const slideCount = slides.length;
      const shouldLoop = slideCount > Math.max(desktopColumns, tabletColumns, mobileColumns);
      
      const swiperConfig = {
        slidesPerView: mobileColumns,
        spaceBetween: 20,
        loop: shouldLoop,
        centeredSlides: mobileColumns === 1 && slideCount === 1,
        breakpoints: {
          768: {
            slidesPerView: Math.min(tabletColumns, slideCount),
            spaceBetween: 25,
            centeredSlides: tabletColumns === 1 && slideCount === 1
          },
          1024: {
            slidesPerView: Math.min(desktopColumns, slideCount),
            spaceBetween: 30,
            centeredSlides: desktopColumns === 1 && slideCount === 1
          }
        }
      };

      // Add data attributes for CSS styling
      sliderContainer.setAttribute('data-desktop-columns', desktopColumns);
      sliderContainer.setAttribute('data-tablet-columns', tabletColumns);
      sliderContainer.setAttribute('data-mobile-columns', mobileColumns);

      // Apply image size configuration
      const contentImages = sliderContainer.querySelectorAll('.tpt-content-image img');
      contentImages.forEach(img => {
        img.style.setProperty('--content-image-size', `${imageSize}px`);
      });

      // Set CSS custom property for this slider
      sliderContainer.style.setProperty('--content-image-size', `${imageSize}px`);

      // Add navigation if enabled and needed
      if (showNavigation && shouldLoop) {
        const prevButton = sliderContainer.querySelector('.swiper-button-prev');
        const nextButton = sliderContainer.querySelector('.swiper-button-next');
        
        if (prevButton && nextButton) {
          swiperConfig.navigation = {
            nextEl: nextButton,
            prevEl: prevButton,
          };
        }
      } else {
        // Hide navigation buttons if not needed
        const prevButton = sliderContainer.querySelector('.swiper-button-prev');
        const nextButton = sliderContainer.querySelector('.swiper-button-next');
        if (prevButton) prevButton.style.display = 'none';
        if (nextButton) nextButton.style.display = 'none';
      }

      // Add pagination if enabled and needed
      if (showPagination && shouldLoop) {
        const pagination = sliderContainer.querySelector('.swiper-pagination');
        if (pagination) {
          swiperConfig.pagination = {
            el: pagination,
            clickable: true,
            type: 'bullets'
          };
        }
      } else {
        // Hide pagination if not needed
        const pagination = sliderContainer.querySelector('.swiper-pagination');
        if (pagination) pagination.style.display = 'none';
      }

      // Add autoplay if enabled and there are enough slides
      if (autoPlay && shouldLoop) {
        swiperConfig.autoplay = {
          delay: autoPlayDelay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        };
      }

      // Initialize Swiper
      try {
        this.swipers[tabOrder] = new Swiper(swiperElement, swiperConfig);
      } catch (error) {
        console.log('Swiper initialization delayed, will retry...');
        // Retry after a short delay if Swiper is not ready
        setTimeout(() => {
          try {
            this.swipers[tabOrder] = new Swiper(swiperElement, swiperConfig);
          } catch (e) {
            console.error('Failed to initialize Swiper:', e);
          }
        }, 100);
      }
    }

    disconnectedCallback() {
      // Clean up swipers when component is removed
      Object.values(this.swipers).forEach(swiper => {
        if (swiper && typeof swiper.destroy === 'function') {
          swiper.destroy();
        }
      });
      this.swipers = {};
    }
  });
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Additional initialization if needed
  const tabComponents = document.querySelectorAll('tpt-tabs-component');
  tabComponents.forEach(component => {
    if (!component.initialized) {
      component.initialized = true;
    }
  });
});

// Handle dynamic content loading (for theme editor)
document.addEventListener('shopify:section:load', function(event) {
  const tabComponents = event.target.querySelectorAll('tpt-tabs-component');
  tabComponents.forEach(component => {
    // Reinitialize component
    if (component.init) {
      component.init();
    }
  });
});

// Handle section unloading (cleanup)
document.addEventListener('shopify:section:unload', function(event) {
  const tabComponents = event.target.querySelectorAll('tpt-tabs-component');
  tabComponents.forEach(component => {
    if (component.disconnectedCallback) {
      component.disconnectedCallback();
    }
  });
});